import { Translate } from 'next-translate';
import { CustomColumnHeader, EventRequest, Global, LineItem, Lot, Round } from 'types';
import { SUGGESTED_PRICE_ENHANCEMENT_PHASE_DEFAULT } from '@enums/flags';
import { createColumnLabelResolver, repaintBuyerExportHeaders } from '@features/event-level-column-rename';
import { REQUEST_TYPE_INFORMATION, REQUEST_TYPE_PRICE_UPDATE } from '@components/bid_request/RequestConstants';
import { getExportColumnNames } from './getExportColumnNames';
import { getCustomColumnHeadersForLot } from './getCustomColumnHeadersForLot';
import { getTranslatedDefaultHeadersMap } from './getTranslatedDefaultHeadersMap';
import { getCustomColumnHeaderIdsOrderMapForLots } from './getCustomColumnHeaderIdsOrderMapForLots';
import { getItemRow, type ItemMap } from './getItemRow';
import { getLineItemRowValues } from './getLineItemRowValues';
import { cleanItemValue } from './cleanItemValue';

interface BuildExportDataParams {
  canImportPostSubmission?: boolean;
  global: Global;
  isSupplier?: boolean;
  lots: Lot[];
  request: EventRequest;
  setBaselineFromQuotes?: boolean;
  showDiscount: boolean;
  showLogisticsCharge: boolean;
  showPricePerUnit: boolean;
  showSuggestedPricePerUnitColumn?: boolean;
  suggestedPriceEnhancementPhase?: number;
  t: Translate;
  useSuggestedPricing?: boolean;
}

/**
 * Builds the data needed for exporting the bid request details.
 */
export const buildExportData = ({
  canImportPostSubmission = false,
  global,
  lots,
  request,
  showPricePerUnit,
  showLogisticsCharge,
  showDiscount,
  showSuggestedPricePerUnitColumn = false,
  suggestedPriceEnhancementPhase = SUGGESTED_PRICE_ENHANCEMENT_PHASE_DEFAULT,
  t,
  setBaselineFromQuotes = false,
  isSupplier = false,
  useSuggestedPricing = false,
}: BuildExportDataParams) => {

  const data = [];
  const isPriceUpdate = request?.request_type === REQUEST_TYPE_PRICE_UPDATE;
  const isInformation = request?.request_type === REQUEST_TYPE_INFORMATION;
  const exportColumnNames = getExportColumnNames({
    canImportPostSubmission,
    hasActiveSubmissions: Boolean(request?.active_bids),
    isInformation,
    isPriceUpdate,
    isSupplier,
    showDiscount,
    showLogisticsCharge,
    showPricePerUnit,
    showSuggestedPricePerUnitColumn,
    suggestedPriceEnhancementPhase,
    useSuggestedPricing,
  });

  const descriptionItemsType = global.lots.custom_column_headers.column_types.descriptive;

  const customColumnHeaders: CustomColumnHeader[] = lots.flatMap((lot: Lot) => {
    const { custom_column_headers: customHeaders = [] } = lot;
    return getCustomColumnHeadersForLot({ customHeaders, global, incomingColumnTypes: [descriptionItemsType], isSupplier });
  });

  // Get unique display text for custom column headers used for keeping order
  const uniqueCustomColumnHeadersDisplayText: string[] = [...new Set(
    customColumnHeaders
      .map((header: CustomColumnHeader) => header.display_text)
  )];

  // Resolve labels once per export, never per row — the export path is load-sensitive.
  const labelFor = createColumnLabelResolver({ labels: request?.line_item_column_labels, t });

  const columnsMap: ItemMap = {
    ...repaintBuyerExportHeaders(
      getTranslatedDefaultHeadersMap(t, canImportPostSubmission, isPriceUpdate),
      labelFor,
      exportColumnNames
    ),
    description: uniqueCustomColumnHeadersDisplayText
  };

  const headersRow = getItemRow(columnsMap, exportColumnNames);
  data.push([t('general.lot_name'), ...headersRow]);

  const customHeaderIdsOrderMap: Record<string, number> = getCustomColumnHeaderIdsOrderMapForLots(customColumnHeaders, uniqueCustomColumnHeadersDisplayText);

  const { rounds = [], multi_round: isMultiRound, event_format: eventFormat } = request.bidding_event || {};
  const isMultipleRoundBidEvent = isMultiRound && eventFormat === 'rounds';
  const currentTime = new Date().toISOString();
  const currentRound = rounds.find((round: Round) => round.start_time < currentTime && round.end_time > currentTime);
  const isCurrentRoundOne = currentRound?.number === 1;

  lots.forEach((lot: Lot) => {
    const { line_items: lineItems = [] } = lot;

    lineItems.forEach((lineItem: LineItem) => {
      const lineItemRow = getLineItemRowValues({
        customColumnHeaders,
        customColumnHeadersDisplayText: uniqueCustomColumnHeadersDisplayText,
        customHeaderIdsOrderMap,
        exportColumnNames,
        global,
        isCurrentRoundOne,
        isMultipleRoundBidEvent,
        isPriceUpdate,
        isSupplier,
        lineItem,
        setBaselineFromQuotes,
      });

      data.push([cleanItemValue(lot.name), ...lineItemRow]);
    });
  });

  return data;
};
